import { Node } from '@xyflow/react';
import Constants from 'js/Constants';
import moment from 'moment';
import { ChildrenReducerOptions, CreateNodesFunction } from './types';

export function createChildrenReducer(item: any, nodes: Node[], options: ChildrenReducerOptions) {
  const {
    pipelineItems,
    expanded,
    parentId,
    isRagFlow = false,
    columnY, // This is the starting Y for the first child,
  } = options;

  const mappedChildren = item.children.map((c: any) => pipelineItems[c]) as any[];

  return (createNodes: CreateNodesFunction) => {
    mappedChildren.forEach((child, index) => {
      if (!child) return;

      if (item.type === 'DATA_SINK' && item.node_type === 'data_sink') {
        child.isReference = true;
      }

      let currentChildY;
      if (index === 0) {
        // The first child starts at the designated column Y.
        currentChildY = columnY;
      }

      createNodes(nodes, child, parentId);
    });
  };
}

export function hasChildren(item: any): boolean {
  return !!item.children?.length;
}

export function shouldCreateNexsetLoader(
  item: any,
  isApiServerFlow: boolean,
  isSqlAutomation?: boolean,
): boolean {
  if (isApiServerFlow) {
    return false;
  }

  const { type, status, children, parent_node_id, created_at } = item;

  if (isSqlAutomation) {
    return !!(type === 'SOURCE' && !parent_node_id);
  }

  return !!(
    type === 'SOURCE' &&
    status === Constants.SourceStatus.ACTIVE &&
    !children?.length &&
    !parent_node_id &&
    !moment(created_at).isBefore(moment().subtract(7, 'days'))
  );
}

/**
 * Whether a DATA_SINK exists anywhere below `item` in its own flow's subtree
 * (walks `children` keys through `pipelineItems`). Scoped to this item on
 * purpose: on a multi-flow canvas `pipelineItems` holds every flow, so a
 * sink belonging to another flow must not count. Used for the BYO source's
 * "Add Destination" shell (node + edge must agree).
 */
export function hasDescendantSink(item: any, pipelineItems: Record<string, any>): boolean {
  const seen = new Set<string>();
  const stack: any[] = [...(item?.children ?? [])];
  while (stack.length) {
    const key = String(stack.pop());
    if (seen.has(key)) continue;
    seen.add(key);
    const child = pipelineItems?.[key];
    if (!child) continue;
    if (child.type === 'DATA_SINK') return true;
    stack.push(...(child.children ?? []));
  }
  return false;
}
